import { FacebookCustomAudienceRule } from '../Rule';

export interface FacebookCustomAudience {
  id: string;
  name: string;
  account_id: string;
  description?: string;
  // Bounded approximate # of people in this audience
  approximate_count_lower_bound: number;
  approximate_count_upper_bound: number;
  // source of customer information in the uploaded file
  customer_file_source: FacebookCustomerFileSource;
  // How the audience was created
  data_source: FacebookDataSource;
  // Indicates whether audience can be used or not
  delivery_status: FacebookDeliveryStatus;
  // Whether the audience is used to seed value based lookalike
  is_value_based: boolean;
  // IDs of lookalike audiences generated from this audience
  lookalike_audiences_ids?: string[];
  // Generated only when subtype is "LOOKALIKE"
  lookalike_spec?: FacebookLookalikeSpec;
  // Last operated performed on an audience
  operation_status: FacebookOperationStatus;
  // Your opt out URL so people can choose to not be targeted
  opt_out_link?: string;
  // FB Pixel ID which is collecting events for this audience
  pixel_id?: string;
  // Number of days to keep the user within the cluster
  // Between 1 and 180
  retention_days: number;
  // Audience rules to be applied on the referrer URL
  rule?: FacebookCustomAudienceRule;
  // Aggregation on top of the rule
  // Examples include count, sum, etc
  rule_aggregation?: object;
  // Type of custom audience derived from original data source
  subtype: CustomAudienceSubType;
  // Last update of people in this audience.  Only supported for
  // Customer Lists
  time_content_updated?: string;
  time_created?: string;
  time_updated?: string;
  prefill?: number; // 0 or 1
}

export enum FacebookCustomerFileSource {
  USER_PROVIDED_ONLY = 'USER_PROVIDED_ONLY',
  PARTNER_PROVIDED_ONLY = 'PARTNER_PROVIDED_ONLY',
  BOTH_USER_AND_PARTNER_PROVIDED = 'BOTH_USER_AND_PARTNER_PROVIDED',
}

export interface FacebookDataSource {
  type: FacebookDataSourceType;
  subType: FacebookDataSourceSubType;
  creation_params: string;
}

export enum FacebookDataSourceType {
  // Audience created without content
  UNKNOWN = 'UNKNOWN',
  // Created directly from a file
  FILE_IMPORTED = 'FILE_IMPORTED',
  // Created from a website event
  EVENT_BASED = 'EVENT_BASED',
  // Created from a lookalike audience
  SEED_BASED = 'SEED_BASED',
  // Created using third party data
  THIRD_PARTY_IMPORTED = 'THIRD_PARTY_IMPORTED',
  // Values pasted into input field manually on FB
  COPY_PASTE = 'COPY_PASTE',
  // Created by an importer
  CONTACT_IMPORTER = 'CONTACT_IMPORTER',
}

export enum FacebookDataSourceSubType {
  // Research poll, experiment, etc
  ANYTHING = 'ANYTHING',
  // Folder
  NOTHING = 'NOTHING',
  // Email hashes or phone hashes
  HASHES = 'HASHES',
  // Facebook User IDs
  USER_IDS = 'USER_IDS',
  // Email/phone hashes OR User IDs
  HASHES_OR_USER_IDS = 'HASHES_OR_USER_IDS',
  // Platform IDs / IDFAs
  MOBILE_ADVERTISER_IDS = 'MOBILE_ADVERTISER_IDS',
  // Created from multiple type of hashes at the same time
  MULTI_HASHES = 'MULTI_HASHES',
  // Events reported by FB pixels on websites
  WEB_PIXEL_HITS = 'WEB_PIXEL_HITS',
  // Events reported on mobile platforms
  MOBILE_APP_EVENTS = 'MOBILE_APP_EVENTS',
  // Composite audience from 1 or more inclusions and exclusion audiences.
  // EG: People who performed 2 or more tracked actions in the app
  MOBILE_APP_COMBINATION_EVENTS = 'MOBILE_APP_COMBINATION_EVENTS',
  // Reports from video views
  // EG: 3% or 95% of video has been watched
  VIDEO_EVENTS = 'VIDEO_EVENTS',
  // Composite audience of 1 or more inclusion and exclusion audiences
  // EG: An audience of people who viewed home but didn't finish sign up
  WEB_PIXEL_COMBINATION_EVENTS = 'WEB_PIXEL_COMBINATION_EVENTS',
  // Created from user IDs
  CUSTOM_AUDIENCE_USERS = 'CUSTOM_AUDIENCE_USERS',
  // Created from page fans
  PAGE_FANS = 'PAGE_FANS',
  // Created from conversions tracked buy FB's pixel
  CONVERSION_PIXEL_HITS = 'CONVERSION_PIXEL_HITS',
  // Users from mobile app
  APP_USERS = 'APP_USERS',
  // Created from saved target group
  S_EXPR = 'S_EXPR',
  // Created from emails imported from Mailchimp
  MAIL_CHIMP_EMAIL_HASHES = 'MAIL_CHIMP_EMAIL_HASHES',
  // Created from emails imported from Constant Contact
  CONSTANT_CONTACTS_EMAIL_HASHES = 'CONSTANT_CONTACTS_EMAIL_HASHES',
  // Emails entered manually via Ads Manager
  COPY_PASTE_EMAIL_HASHES = 'COPY_PASTE_EMAIL_HASHES',
  // Get in touch with the importer
  CONTACT_IMPORTER = 'CONTACT_IMPORTER',
}

export const exampleFacebookDataSource: FacebookDataSource = {
  type: FacebookDataSourceType.CONTACT_IMPORTER,
  subType: FacebookDataSourceSubType.ANYTHING,
  creation_params: '',
};

export interface FacebookDeliveryStatus {
  code: FacebookDeliveryStatusCode;
  description: string;
}

export enum FacebookDeliveryStatusCode {
  // Active and ready to be used
  ACTIVE = 200,
  // Audience is too small and is disabled
  TOO_SMALL = 300,
  // Audience is not usable for a variety of reasons including policy violation
  NOT_USABLE = 400,
}

export const exampleFacebookDeliveryStatus: FacebookDeliveryStatus = {
  code: FacebookDeliveryStatusCode.ACTIVE,
  description: '',
};

export interface FacebookLookalikeSpec {
  country: string;
  // Is this used in financial service
  is_financial_service: boolean;
  // ID of origin audience - must be larger than 100 approximate size
  origin: string;
  origin_event_name: string;
  // Name of origin event source.  For example a product catalog
  // Based lookalike, this field will be the product catalog's name
  origin_event_source_name: string;
  // Type of the origin event sources for value based LAL
  origin_event_source_type: string;
  // Name of product set that is used to create the lookalike
  // Field only exists for a product catalog based lookalike
  product_set_name?: string;
  // Range 0.01 to 0.20
  // Indicates top % of original audience in selected country
  // Default value is 0.01 which is 1%
  ratio: number;
  // Used for tiered lookalikes
  starting_ratio?: number;
  target_countries: string[];
  target_country_names: string[];
  type: 'similarity' | 'reach';
}

export interface FacebookOperationStatus {
  code: FacebookOperationStatusCode;
  description: string;
}

export enum FacebookOperationStatusCode {
  // Status not available
  Not_Available = 0,
  // Normal  No issues found
  Normal = 200,
  // Warning.  There is a message to let advertisers know
  Warning = 400,
  // No file has been uploaded
  Missing_Upload = 410,
  // Low match rate
  Low_Match_Rate = 411,
  // High rate of invalid entries in last upload.  There
  // Is unusable customer data
  Invalid_Entries = 412,
  // No FB Pixel setup
  Missing_Pixel = 421,
  // Pixel not firing
  Pixel_Not_Firing = 422,
  //Invalid Pixel
  Invalid_Pixel = 423,
  // Lookalike audience refresh failed
  Lookalike_Refresh_Failed = 431,
  // Lookalike audience build failed
  Lookalike_Build_Failed = 432,
  // Lookalike audience failed a different way?
  Lookalike_Build_Failed_Again = 433,
  // Lookalike audience build retrying
  Lookalike_Build_Retrying = 434,
  // FB is still filling the audience with people
  // And this audience may grow during use
  // Audience may still be used
  Finding_People = 441,
  // Custom audience could not be prefilled
  Prefill_Error = 442,
  // Audience hasn't been used for at least a month in an ad or was made
  // Over 90 days ago and is out of date
  Out_Of_Date = 450,
  // Account created with this audience is no longer active
  Account_Inactive = 470,
  // Error
  Error = 500,
}

export const exampleFacebookOperationStatus: FacebookOperationStatus = {
  code: FacebookOperationStatusCode.Normal,
  description: '',
};

export enum CustomAudienceSubType {
  CUSTOM = 'CUSTOM',
  WEBSITE = 'WEBSITE',
  APP = 'APP',
  OFFLINE_CONVERSION = 'OFFLINE_CONVERSION',
  CLAIM = 'CLAIM',
  PARTNER = 'PARTNER',
  MANAGED = 'MANAGED',
  VIDEO = 'VIDEO',
  LOOKALIKE = 'LOOKALIKE',
  ENGAGEMENT = 'ENGAGEMENT',
  BAG_OF_ACCOUNTS = 'BAG_OF_ACCOUNTS',
  STUDY_RULE_AUDIENCE = 'STUDY_RULE_AUDIENCE',
  FOX = 'FOX',
  MEASUREMENT = 'MEASUREMENT',
}
